import { useTimeProgression } from './useTimeProgression';
import { devLog, devWarn } from '@/lib/devPerfLog';

/**
 * useWallet - Unified wallet hook for all money operations
 * 
 * All money transactions in the app should go through this hook.
 * It uses player_financial_status as the single source of truth.
 * 
 * IMPORTANT: This hook internally uses useTimeProgression which must be 
 * initialized (financialStatus loaded) before earnMoney/spendMoney will work.
 * Check `walletLoaded` before calling money operations from other hooks.
 * 
 * Design decision: 1 point = 1 € in the money system.
 */
export const useWallet = () => {
  const { financialStatus, earnMoney, spendMoney, loadTimeProgression, isInitialLoading: loading } = useTimeProgression();

  /**
   * Safely earn money - logs warning and retries initialization if wallet not ready.
   * Returns true on success, false on failure.
   */
  const safeEarnMoney = async (amount: number): Promise<boolean> => {
    if (!financialStatus) {
      devWarn('Wallet', 'earnMoney: financialStatus not loaded yet', { amount });
      // Attempt to reload if loading is false (means it failed or hasn't started)
      if (!loading) {
        devLog('Wallet', 'Attempting to reload financial status...');
        await loadTimeProgression();
      }
      return false;
    }
    
    devLog('Wallet', 'earnMoney calling', { amount });
    return earnMoney(amount);
  };

  /**
   * Safely spend money - logs warning if wallet not ready.
   * Returns true on success, false on failure.
   */
  const safeSpendMoney = async (amount: number): Promise<boolean> => {
    if (!financialStatus) {
      devWarn('Wallet', 'spendMoney: financialStatus not loaded yet', { amount });
      if (!loading) {
        devLog('Wallet', 'Attempting to reload financial status...');
        await loadTimeProgression();
      }
      return false;
    }
    return spendMoney(amount);
  };

  return {
    // Current balance from the canonical source (player_financial_status.current_money)
    currentMoney: financialStatus?.current_money ?? 0,
    // Total earned (cumulative, excluding starting money)
    totalEarned: financialStatus?.total_earned ?? 0,
    // Total spent (cumulative)
    totalSpent: financialStatus?.total_spent ?? 0,
    // Earn money - adds to current_money and total_earned
    earnMoney: safeEarnMoney,
    // Spend money - subtracts from current_money and adds to total_spent
    spendMoney: safeSpendMoney,
    // Refresh wallet data from DB
    refresh: loadTimeProgression,
    // Loading state - true while loading
    loading,
    // Wallet loaded - true when financialStatus is available and ready for transactions
    walletLoaded: financialStatus !== null,
    // Deprecated: use walletLoaded instead
    isLoaded: financialStatus !== null
  };
};
